import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ConfigGeneral.scss';
import Tabs from 'devextreme-react/tabs';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { useAppSelector } from '../../contexts/hooks';
import { Config_General } from '../../types/types.config';
import { GroupsConfigurations } from '../../utils/systemTypes';
import { Form, Label, Item as ControlItem, GroupItem } from 'devextreme-react/form';
import { Button, ScrollView, Tooltip, LoadIndicator, TextBox } from 'devextreme-react';
import { newToast } from '../../components';
import { RequiredRule, CustomRule } from 'devextreme-react/data-grid';
import { useApiConfiguration } from '../../api/api.configuration';
import { formatMessage } from 'devextreme/localization';
import { useScreenSize } from '../../utils/media-query';

// Todas las pestañas se renderizan como acordeón (una tarjeta plegable por
// subgrupo) EXCEPTO "MÓDULOS", que conserva el formulario plano clásico.
const MODULOS_GROUP = 6;

const Index = () => {
  const Prm_Conf = useAppSelector((x) => x.userInfo.Permissions?.Prm_Conf);
  const [Configuracion, setConfiguracion] = useState<Config_General[]>([]);
  const [currentGroup, setCurrentGroup] = useState<number>(1);
  const [EditItem, setEditItem] = useState<boolean>(true);
  const [data, setData] = useState<any>({});
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Texto del buscador global y títulos de subgrupos de servicios expandidos.
  const [searchText, setSearchText] = useState<string>('');
  const [expandedTitles, setExpandedTitles] = useState<string[]>([]);
  const { getConfigGeneral, setConfig } = useApiConfiguration();
  const frmRef = useRef<Form>(null);
  // Refs de los Forms del acordeón de SERVICIOS (uno por subgrupo). Se usan para
  // validar todas las secciones al guardar, estén expandidas o colapsadas.
  const serviceFormRefs = useRef<Record<string, Form>>({});
  // Snapshot "prístino": los valores tal cual llegaron del servidor. Al guardar
  // se compara contra esto para enviar SOLO lo que cambió.
  const initialDataRef = useRef<Record<string, any>>({});
  // Valores vivos del formulario, actualizados vía onFieldDataChanged. Es la
  // única fuente de verdad al guardar, independiente de si el render es un Form
  // único (otras pestañas) o varios Forms (acordeón de servicios).
  const liveDataRef = useRef<Record<string, any>>({});
  const { isXSmall, isSmall, isMedium, isLarge } = useScreenSize();
  // En pantallas pequeñas los botones de acción se guardan en el menú de overflow
  // (los 3 puntos) de la toolbar; en pantallas grandes se muestran normalmente.
  const actionsLocateInMenu = isXSmall || isSmall ? 'always' : 'auto';

  // Mantiene liveDataRef sincronizado con cualquier edición, venga del Form que
  // venga (texto, select, switch...).
  const handleFieldDataChanged = useCallback((e: any) => {
    if (e?.dataField !== undefined) {
      liveDataRef.current[e.dataField] = e.value;
    }
  }, []);

  // Construye el objeto del formulario a partir de la lista de configuración.
  const buildFormData = useCallback((list: Config_General[]) => {
    const formData: Record<string, any> = {};
    list.forEach((item) => {
      formData[item.Cgn_Nombre] =
        item.Cgn_Control === 'dxSwitch'
          ? item.Cgn_Resultado === 'true' // Convertir string a booleano
          : item.Cgn_Resultado;
    });
    return formData;
  }, []);

  // Carga (o recarga) la configuración SIEMPRE fresca desde el servidor en vez de
  // confiar en el snapshot persistido de Redux, que puede estar desfasado.
  const loadConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const list = await getConfigGeneral();
      setConfiguracion(list);
      const formData = buildFormData(list);
      initialDataRef.current = { ...formData }; // clon independiente para el diff
      liveDataRef.current = { ...formData }; // fuente de verdad de ediciones
      setData(formData);
      frmRef.current?.instance.updateData(formData);
    } finally {
      setIsLoading(false);
    }
  }, [getConfigGeneral, buildFormData]);

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ¿El item de configuración coincide con el texto buscado? Busca en label,
  // nombre técnico, descripción y subgrupo.
  const itemMatches = useCallback((item: Config_General, q: string) => {
    if (!q) return false;
    const haystack = `${item.Cgn_Label ?? ''} ${item.Cgn_Nombre ?? ''} ${item.Cgn_Descripcion ?? ''} ${item.Cgn_SubGrupo ?? ''}`.toLowerCase();
    return haystack.includes(q);
  }, []);

  // Secciones del acordeón del grupo actual (subgrupo -> items). Los items sin
  // subgrupo caen en "General".
  const currentSections = useMemo(() => {
    const cfg = Configuracion.filter((item) => item.Cng_Grupo === currentGroup);
    const grouped = cfg.reduce((groups, item) => {
      const subGroup = item.Cgn_SubGrupo || 'General';
      (groups[subGroup] = groups[subGroup] || []).push(item);
      return groups;
    }, {} as Record<string, Config_General[]>);
    return Object.entries(grouped).map(([title, items]) => ({ title, items }));
  }, [Configuracion, currentGroup]);

  // Conjunto de grupos (pestañas) que contienen algún resultado. null = sin
  // búsqueda activa (todas las pestañas habilitadas y sin resaltar).
  const matchedGroups = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return null;
    const set = new Set<number>();
    Configuracion.forEach((item) => {
      if (itemMatches(item, q)) set.add(item.Cng_Grupo);
    });
    return set;
  }, [searchText, Configuracion, itemMatches]);

  // dataSource de las pestañas: deshabilita las que no tienen resultados cuando
  // hay búsqueda activa.
  const tabsDataSource = useMemo(
    () =>
      GroupsConfigurations.map((g) => ({
        ...g,
        disabled: matchedGroups ? !matchedGroups.has(g.Value) : false,
      })),
    [matchedGroups]
  );

  // Sin búsqueda: todas las secciones abiertas por defecto. Con búsqueda: solo
  // las que coinciden (por sus campos o por el nombre del subgrupo).
  useEffect(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) {
      setExpandedTitles(currentSections.map((s) => s.title));
      return;
    }
    const titles = currentSections.filter((s) => s.title.toLowerCase().includes(q) || s.items.some((it) => itemMatches(it, q))).map((s) => s.title);
    setExpandedTitles(titles);
  }, [searchText, currentSections, itemMatches]);

  // Expande/colapsa una sección del acordeón.
  const toggleSection = useCallback((title: string) => {
    setExpandedTitles((prev) => (prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]));
  }, []);

  const handleClicGroup = useCallback((e) => {
    setCurrentGroup(e.itemData.Value);
    // El efecto de expansión reajusta las secciones para el nuevo grupo (todas
    // abiertas por defecto, o las coincidentes si hay búsqueda activa).
  }, []);

  // ¿Están todas las secciones del grupo actual expandidas?
  const allSectionsExpanded = currentSections.length > 0 && currentSections.every((s) => expandedTitles.includes(s.title));

  // Botón "Contraer/Expandir todo".
  const toggleAllSections = useCallback(() => {
    setExpandedTitles((prev) => {
      const allOpen = currentSections.length > 0 && currentSections.every((s) => prev.includes(s.title));
      return allOpen ? [] : currentSections.map((s) => s.title);
    });
  }, [currentSections]);

  const onTabsSelectionChanged = useCallback(
    async (args) => {
      const currentForm = frmRef.current?.instance;

      if (currentForm) {
        const validationResult = currentForm.validate();

        // Asegurar que hay reglas rotas
        if (validationResult?.brokenRules?.length && EditItem) {
          // Filtrar campos inválidos
          const invalidFields = validationResult.brokenRules.filter((rule) => {
            const fieldName = rule.type; // Obtener el nombre del campo directamente
            return (
              rule.type === 'required' &&
              fieldName && // Verificar que el campo existe
              (data[fieldName] === undefined || data[fieldName] === null || data[fieldName] === '')
            );
          });

          // Mostrar mensaje solo si hay campos realmente inválidos
          if (invalidFields.length > 0) {
            newToast('WARNING', 'Hay campos obligatorios pendientes por llenar', 5);
            return;
          }
        }
      }

      if (typeof args.value === 'number') {
        setSelectedTab(args.value);
      }
    },
    [frmRef, EditItem, data]
  );

  const handleEditClick = useCallback(() => {
    if (!Prm_Conf) {
      newToast('WARNING', 'No cuenta con permisos para editar la configuración', 5);
      setEditItem(true);
      return;
    }
    setEditItem(false);
  }, []);

  const handleSaveClick = useCallback(async () => {
    // Validar el/los formulario(s) montado(s): el Form único de las demás
    // pestañas, o todos los Forms del acordeón de servicios.
    const forms = currentGroup === MODULOS_GROUP ? [frmRef.current] : Object.values(serviceFormRefs.current);
    const mountedForms = forms.filter((f) => f?.instance);

    // Validar solo los Forms montados (en servicios, las secciones expandidas).
    const isValid = mountedForms.every((f) => f!.instance.validate().isValid);
    if (!isValid) {
      newToast('WARNING', 'Ingrese todos los datos requeridos', 5);
      return;
    }

    // Quedarnos SOLO con los campos que cambiaron respecto al snapshot prístino
    // del servidor. liveDataRef refleja las ediciones de cualquier Form.
    const formData = liveDataRef.current;
    const changed: Record<string, string> = {};
    Object.keys(formData).forEach((key) => {
      const current = formData[key];
      const initial = initialDataRef.current[key];
      if (current !== initial) {
        changed[key] = typeof current === 'boolean' ? String(current) : current ?? '';
      }
    });

    if (Object.keys(changed).length === 0) {
      newToast('INFO', 'No hay cambios por guardar', 3);
      setEditItem(true);
      return;
    }

    setIsSaving(true);
    try {
      const ok = await setConfig(changed);
      if (ok) {
        // Recargar del servidor: refleja lo guardado y también cambios que otros
        // usuarios hayan hecho, y resetea el snapshot prístino para el próximo diff.
        await loadConfig();
        setEditItem(true);
      }
    } catch (error) {
      console.error('Error al guardar configuración:', error);
    } finally {
      setIsSaving(false);
    }
  }, [setConfig, loadConfig, currentGroup]);

  // Función para renderizar un control individual
  const renderControlItem = (item: Config_General) => {
    const isPasswordField = item.Cgn_EsContrasena === true;
    // Si el campo coincide con la búsqueda, se marca para resaltar su etiqueta.
    const q = searchText.trim().toLowerCase();
    const hitClass = q && itemMatches(item, q) ? 'field-search-hit' : undefined;

    switch (item.Cgn_Control) {
      case 'dxSwitch':
        return (
          <ControlItem
            key={item.Cgn_Id}
            cssClass={hitClass}
            editorType={item.Cgn_Control}
            editorOptions={{
              value: data[item.Cgn_Nombre] === true || data[item.Cgn_Nombre] === 'true',
              onValueChanged: (e) => {
                const newValue = e.value === 'true' ? true : e.value === 'false' ? false : e.value;
                liveDataRef.current[item.Cgn_Nombre] = newValue; // fuente de verdad al guardar
                setData((prev) => {
                  const updatedData = { ...prev, [item.Cgn_Nombre]: newValue };
                  return updatedData;
                });
              },
            }}
            disabled={EditItem}
            dataField={item.Cgn_Nombre}
            helpText={item.Cgn_Descripcion ? item.Cgn_Descripcion : ''}>
            <Label text={item.Cgn_Label?.toUpperCase()} />
            <CustomRule validationCallback={(e) => typeof e.value === 'boolean'} message='El campo es obligatorio' />
          </ControlItem>
        );
      case 'dxSelectBox':
        return (
          <ControlItem
            key={item.Cgn_Id}
            cssClass={hitClass}
            editorType={item.Cgn_Control}
            editorOptions={{
              items: JSON.parse(item.Cng_ControlListas),
              displayExpr: 'label',
              valueExpr: 'value',
            }}
            disabled={EditItem}
            dataField={item.Cgn_Nombre}
            helpText={item.Cgn_Descripcion ? item.Cgn_Descripcion : ''}>
            <Label text={item.Cgn_Label?.toUpperCase()} />
            {item.Cng_Requerido === true && <RequiredRule />}
          </ControlItem>
        );
      case 'dxTagBox':
        return (
          <ControlItem
            key={item.Cgn_Id}
            cssClass={hitClass}
            dataField={item.Cgn_Nombre}
            editorType='dxTextBox'
            disabled={EditItem}
            helpText={item.Cgn_Descripcion ? item.Cgn_Descripcion : ''}>
            <Label text={item.Cgn_Label?.toUpperCase()} />
            {item.Cng_Requerido === true && <RequiredRule />}
          </ControlItem>
        );
      default:
        return (
          <ControlItem key={item.Cgn_Id} cssClass={hitClass} editorType={isPasswordField ? 'dxTextBox' : item.Cgn_Control} editorOptions={isPasswordField ? { mode: 'password' } : undefined} disabled={EditItem} dataField={item.Cgn_Nombre} helpText={item.Cgn_Descripcion ? item.Cgn_Descripcion : ''}>
            <Label text={item.Cgn_Label?.toUpperCase()} />
            {item.Cng_Requerido === true && <RequiredRule />}
          </ControlItem>
        );
    }
  };

  // Función para renderizar los items del formulario agrupados
  const renderFormItems = () => {
    // Filtrar configuraciones del grupo actual
    const currentGroupConfig = Configuracion.filter((item) => item.Cng_Grupo === currentGroup);

    // Si no hay configuraciones para el grupo actual, devolver array vacío para mostrar formulario vacío
    if (currentGroupConfig.length === 0) {
      return [];
    }

    // Agrupar por Cgn_SubGrupo
    const groupedConfig = currentGroupConfig.reduce((groups, item) => {
      const subGroup = item.Cgn_SubGrupo || 'Sin Grupo';
      if (!groups[subGroup]) {
        groups[subGroup] = [];
      }
      groups[subGroup].push(item);
      return groups;
    }, {} as Record<string, Config_General[]>);

    // Si solo hay un grupo y es "Sin Grupo", renderizar sin agrupación
    const groupKeys = Object.keys(groupedConfig);
    if (groupKeys.length === 1 && groupKeys[0] === 'Sin Grupo') {
      return groupedConfig['Sin Grupo'].map(renderControlItem);
    }

    // Renderizar con agrupación - subgrupos en una columna, campos en 2 columnas
    return Object.entries(groupedConfig).map(([subGroupName, items]) => (
      <GroupItem key={subGroupName} caption={subGroupName !== 'Sin Grupo' ? subGroupName : undefined} colCount={2} colCountByScreen={{ sm: 1, md: 2 }}>
        {items.map(renderControlItem)}
      </GroupItem>
    ));
  };

  // Renderiza la pestaña SERVICIOS como un acordeón: una sección plegable por
  // subgrupo (cada integración / servicio externo). Así se ven todos los títulos
  // de un vistazo y se expande solo el que interesa, en vez de un formulario
  // gigante con scroll. Cada sección es su propio Form ligado al mismo `data`.
  // Acordeón propio (divs + estado), en vez del de DevExtreme, para tener control
  // total del espaciado. Cada sección = un subgrupo, con su propio Form ligado al
  // mismo `data`. Se monta el Form solo al expandir. Se usa en todas las pestañas
  // salvo MÓDULOS.
  const renderAccordion = () => {
    if (currentSections.length === 0) {
      return <div style={{ padding: '20px' }}>No hay configuración en esta sección.</div>;
    }

    const q = searchText.trim().toLowerCase();

    return (
      <div className='services-accordion'>
        {currentSections.map((section) => {
          const isOpen = expandedTitles.includes(section.title);
          const hit = !!q && (section.title.toLowerCase().includes(q) || section.items.some((it) => itemMatches(it, q)));

          return (
            <div className={`service-section${isOpen ? ' service-section--open' : ''}`} key={section.title}>
              <button type='button' className='service-section__header' onClick={() => toggleSection(section.title)}>
                <span className={`accordion-service-title${hit ? ' search-hit' : ''}`}>{section.title}</span>
                <i className={`dx-icon dx-icon-chevron${isOpen ? 'up' : 'down'} service-section__chevron`} />
              </button>

              {isOpen && (
                <div className='service-section__body'>
                  <Form
                    formData={data}
                    ref={(r) => {
                      if (r) serviceFormRefs.current[section.title] = r;
                      else delete serviceFormRefs.current[section.title];
                    }}
                    onFieldDataChanged={handleFieldDataChanged}
                    labelLocation='top'
                    minColWidth={233}
                    colCount={2}
                    colCountByScreen={{ sm: 1, md: 2 }}>
                    {section.items.map(renderControlItem)}
                  </Form>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className='view-wrapper view-wrapper-task-list'>
      <ScrollView className='view-wrapper-scroll' height={'100%'} width={'100%'}>
        <Toolbar className='toolbar-common' multiline={isLarge || isMedium}>
          <Item location='before' visible={isLarge || isMedium}>
            <span className='toolbar-header'>Configuración general</span>
          </Item>
          <Item location='before' widget='dxTabs' cssClass='tabs-container'>
            <Tabs
              defaultSelectedIndex={0}
              dataSource={tabsDataSource}
              width={isLarge || isMedium ? 'auto' : 300}
              onItemClick={handleClicGroup}
              onOptionChanged={onTabsSelectionChanged}
              scrollByContent={true}
              showNavButtons={true}
              scrollingEnabled={true}
              itemRender={(g: any) => <span className={matchedGroups && matchedGroups.has(g.Value) ? 'search-hit' : undefined}>{g.text}</span>}
            />
          </Item>
          <Item location='after' locateInMenu='never' visible={isLarge || isMedium}>
            <TextBox
              value={searchText}
              onValueChanged={(e) => setSearchText(e.value ?? '')}
              placeholder='Buscar...'
              mode='search'
              showClearButton={true}
              width={isLarge ? 240 : 180}
              stylingMode='outlined'
            />
          </Item>
          <Item location='after' locateInMenu={actionsLocateInMenu} widget='dxButton' showText='inMenu'>
            <Button
              id='toolBarToggleAll'
              text={allSectionsExpanded ? 'Contraer todo' : 'Expandir todo'}
              stylingMode='outlined'
              icon={allSectionsExpanded ? 'chevronup' : 'chevrondown'}
              type='default'
              onClick={toggleAllSections}
              visible={currentGroup !== MODULOS_GROUP && !isLoading && !isSaving}
            />
          </Item>
          <Item location='after' locateInMenu={actionsLocateInMenu} widget='dxButton' showText='inMenu'>
            <Button id='toolBarEdit' text={formatMessage('toolBarEdit')} stylingMode='outlined' icon='edit' type='default' onClick={handleEditClick} visible={EditItem && !isSaving} />
          </Item>
          <Item location='after' locateInMenu={actionsLocateInMenu} widget='dxButton' showText='inMenu'>
            <Button id='toolBarSave' text={formatMessage('toolBarSave')} stylingMode='outlined' icon='save' type='default' onClick={handleSaveClick} visible={!EditItem && !isSaving} />
          </Item>
          <Item location='after' locateInMenu='never' visible={isSaving}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LoadIndicator height={20} width={20} />
              <span>Guardando...</span>
            </div>
          </Item>
        </Toolbar>
        {(isXSmall || isSmall) && (
          <div className='config-search-mobile'>
            <TextBox
              value={searchText}
              onValueChanged={(e) => setSearchText(e.value ?? '')}
              placeholder='Buscar configuración...'
              mode='search'
              showClearButton={true}
              width='100%'
              stylingMode='outlined'
            />
          </div>
        )}
        <div className='formStyle'>
          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '40px' }}>
              <LoadIndicator height={24} width={24} />
              <span>Cargando configuración...</span>
            </div>
          ) : currentGroup !== MODULOS_GROUP ? (
            renderAccordion()
          ) : (
            <form>
              <Form formData={data} ref={frmRef} onFieldDataChanged={handleFieldDataChanged} labelLocation='top' minColWidth={233} colCount={1} screenByWidth={screenByWidth}>
                {renderFormItems()}
              </Form>
            </form>
          )}
        </div>
      </ScrollView>
      <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#toolBarSave'}>
        {formatMessage('toolBarSave')}
      </Tooltip>
      <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#toolBarEdit'}>
        {formatMessage('toolBarEdit')}
      </Tooltip>
    </div>
  );
};

function screenByWidth(width) {
  return width < 720 ? 'sm' : 'md';
}

export default Index;
